import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight, Shield, Briefcase, HardHat, RefreshCw } from 'lucide-react';
import { apiService } from '@/lib/api';
import { setLenderActor, roleLabel, LENDER_BLUE, LENDER_NAVY } from '@/lib/lenderDemo';

const ROLE_META = {
  bank_admin: { icon: Shield, blurb: 'Adds bank users and assigns loans to loan officers.' },
  loan_officer: { icon: Briefcase, blurb: 'Reviews and gives first approval on their own loans only.' },
  construction_dept: { icon: HardHat, blurb: 'Sees every loan at the bank and gives final approval.' },
};

export default function LenderDemoEntry() {
  const navigate = useNavigate();
  const [bank, setBank] = useState(null);
  const [users, setUsers] = useState([]);
  const [sampleBorrower, setSampleBorrower] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState('');

  useEffect(() => {
    apiService.lenderDemoEntry()
      .then(({ data }) => { setBank(data.bank); setUsers(data.users || []); setSampleBorrower(data.sample_borrower); })
      .catch(() => setError("Lender demo isn't available in this environment yet."))
      .finally(() => setLoading(false));
  }, []);

  const enterAs = (u) => {
    setLenderActor({ id: u.id, name: u.name, role: u.role, bank_name: bank?.name });
    navigate(u.role === 'bank_admin' ? '/lender/bank-users' : '/lender/loans');
  };

  const resetDemo = async () => {
    if (!window.confirm('Reset the demo to a clean, freshly-seeded state? This clears any practice draws and users you added.')) return;
    setResetting(true); setResetMsg('');
    try {
      const { data } = await apiService.lenderResetDemo();
      setBank(data.bank); setUsers(data.users || []); setSampleBorrower(data.sample_borrower);
      setResetMsg('Demo reset to a clean state — ready to present.');
    } catch {
      setResetMsg('Could not reset the demo. Please try again.');
    } finally { setResetting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F5F9' }} className="flex items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: LENDER_BLUE }}>D</div>
            <span className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>DrawReady</span>
          </div>
          <h1 className="text-xl font-bold" style={{ color: LENDER_NAVY }}>Lender dashboard — demo</h1>
          <p className="text-slate-500 text-sm mt-1 flex items-center justify-center gap-1.5">
            <Building2 className="w-4 h-4" /> {bank?.name || 'Ohnward Bank & Trust'} · choose who to explore as
          </p>
        </div>

        {loading && <p className="text-center text-slate-400">Loading demo…</p>}
        {error && <p className="text-center text-red-500" data-testid="lender-demo-error">{error}</p>}

        <div className="grid sm:grid-cols-2 gap-3">
          {users.map((u) => {
            const Meta = ROLE_META[u.role] || {};
            const Icon = Meta.icon || Briefcase;
            return (
              <button
                key={u.id}
                onClick={() => enterAs(u)}
                data-testid={`lender-enter-${u.id}`}
                className="group text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EEF3FF', color: LENDER_BLUE }}><Icon className="w-5 h-5" /></span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </div>
                <div className="font-semibold" style={{ color: LENDER_NAVY }}>{u.name}</div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1.5">{roleLabel(u.role)}</div>
                <div className="text-sm text-slate-500">{Meta.blurb}</div>
              </button>
            );
          })}
        </div>

        {sampleBorrower && (
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 text-center">Or see the borrower side</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/lender/apply')}
                data-testid="lender-enter-apply"
                className="group text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold" style={{ color: LENDER_NAVY }}>Apply for funding</div>
                    <div className="text-sm text-slate-500">Submit a project to a lender — the full origination flow.</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </div>
              </button>
              <button
                onClick={() => navigate(`/lender/borrower/${sampleBorrower.id}`)}
                data-testid="lender-enter-borrower"
                className="group text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold" style={{ color: LENDER_NAVY }}>{sampleBorrower.borrower_name} <span className="text-xs font-normal text-slate-400">(funded)</span></div>
                    <div className="text-sm text-slate-500">Draw against the bid sheet — then switch to Travis to review.</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </div>
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <button onClick={resetDemo} disabled={resetting} data-testid="lender-reset-demo"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-600 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${resetting ? 'animate-spin' : ''}`} />
            {resetting ? 'Resetting…' : 'Reset demo to a clean state'}
          </button>
          {resetMsg && <p className="text-xs mt-2" style={{ color: LENDER_BLUE }} data-testid="lender-reset-msg">{resetMsg}</p>}
        </div>
      </div>
    </div>
  );
}
